import React, { useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { addBreadcrumb } from '@sentry/react';
import { SentryContext } from '@/contexts/SentryContext';
import { addApiBreadcrumb as addApiBreadcrumbUtil } from '@/utils/sentry';

interface SentryProviderProps {
  children: React.ReactNode;
}

export const SentryProvider: React.FC<SentryProviderProps> = ({ children }) => {
  const location = useLocation();

  // Add navigation breadcrumbs
  useEffect(() => {
    addBreadcrumb({
      message: `Navigated to ${location.pathname}`,
      category: 'navigation',
      level: 'info',
      data: {
        pathname: location.pathname,
        search: location.search,
      },
    });
  }, [location.pathname, location.search]);

  const addNavigationBreadcrumb = useCallback((to: string) => {
    addBreadcrumb({
      message: `Navigation: ${to}`,
      category: 'navigation',
      level: 'info',
      data: { to },
    });
  }, []);

  const addApiBreadcrumb = useCallback(
    (method: string, endpoint: string, status?: number) =>
      addApiBreadcrumbUtil(method, endpoint, status),
    []
  );

  const contextValue = useMemo(
    () => ({ addNavigationBreadcrumb, addApiBreadcrumb }),
    [addNavigationBreadcrumb, addApiBreadcrumb]
  );

  return (
    <SentryContext.Provider value={contextValue}>
      {children}
    </SentryContext.Provider>
  );
};
